<template lang='pug'>
  div
    v-toolbar(dense)
      v-spacer
      v-spacer
      v-text-field#usersSearch(v-model='search' ref='search' placeholder='Search ("/" to focus)' hide-details prepend-inner-icon='search' clearable single-line)
    table-list(:headers='headers'
      :items='users'
      :total='total'
      :loading='loading'
      :select-all='false'
      :has-selection='false'
      @update='update')
      template(v-slot:row='{item, index}')
        td(@click.right.stop.prevent='assumeUser(item._id)')
          router-link(:to='`users/${item._id}`') {{item.email}}
        td {{item.firstName}}
        td {{item.lastName}}
        td {{item.account.name}}
        td
          v-icon(v-if='item.accountAdmin') check
        td {{item.gitBranch}}
        td
          time-ago(:date='item.lastActive')
        td
          time-ago(:date='item.createdAt')
        td
          v-icon(v-if='item.enabled') check
        td
          v-row(justify='end')
            v-tooltip(bottom)
              template(v-slot:activator='{on}')
                v-btn.assumeUser(v-on='on' text icon @click='assumeUser(item._id)')
                  v-icon supervised_user_circle
              span Assume User
            v-tooltip(bottom)
              template(v-slot:activator='{on}')
                v-btn.editUser(v-on='on' text icon @click='detailItem(item._id)')
                  v-icon edit
              span User Details
            v-menu(offset-y @click.native.stop='' left).mr-3
              template(v-slot:activator='{on}')
                v-btn(text icon v-on='on')
                  v-icon more_vert
              v-list
                v-list-item(@click='disableUser(item)')
                  v-list-item-title(v-if='item.enabled')
                    | Disable
                  v-list-item-title(v-if='!item.enabled')
                    | Enable
      template(v-slot:item='{item}')
        v-row(no-gutters)
          v-col(cols='12').text-body-1.pa-3
            .text-subtitle-1 {{item.name}}
            v-row(no-gutters)
              v-col.text-body-2(cols='4' sm='3') _id:
              v-col {{item._id}}
            v-row(no-gutters)
              v-col.text-body-2(cols='4' sm='3') Email:
              v-col {{item.email}}
            v-row(no-gutters)
              v-col.text-body-2(cols='4' sm='3') First Name:
              v-col {{item.firstName}}
            v-row(no-gutters)
              v-col.text-body-2(cols='4' sm='3') Last Name:
              v-col {{item.lastName}}
            v-row(no-gutters)
              v-col.text-body-2(cols='4' sm='3') Account:
              v-col {{item.account.name}}
                span(v-if='item.accountAdmin') &nbsp;(admin)
            v-row(no-gutters)
              v-col.text-body-2(cols='4' sm='3') Git Branch:
              v-col {{item.gitBranch}}
            v-row(no-gutters)
              v-col.text-body-2(cols='4' sm='3') Last Active:
              v-col
                time-ago-reverse(:date='item.lastActive')
            v-row(no-gutters)
              v-col.text-body-2(cols='4' sm='3') Created:
              v-col
                time-ago-reverse(:date='item.createdAt')
            v-row(no-gutters)
              v-col.text-body-2(cols='4' sm='3') Enabled:
              v-col
                v-icon(v-if='item.enabled') check
            v-row(justify='end')
              v-tooltip(bottom)
                template(v-slot:activator='{on}')
                  v-btn(v-on='on' text icon @click='assumeUser(item._id)')
                    v-icon supervised_user_circle
                span Assume User
              v-tooltip(bottom)
                template(v-slot:activator='{on}')
                  v-btn(v-on='on' text icon @click='detailItem(item._id)')
                    v-icon edit
                span User Details
              v-menu(offset-y @click.native.stop='' left).mr-3
                template(v-slot:activator='{on}')
                  v-btn(text icon v-on='on')
                    v-icon more_vert
                v-list
                  v-list-item(@click='disableUser(item)')
                    v-list-item-title(v-if='item.enabled')
                      | Disable
                    v-list-item-title(v-if='!item.enabled')
                      | Enable
        v-divider
</template>

<script>
import _ from 'lodash';
import {mapActions} from 'vuex';
import SuperAdmin from '@/api/superAdmin';
import Notifications from '@/services/notification.service';
import TableList from '@/components/TableList';
import TableMixin from '@/mixins/table.mixin';
import FocusMixin from '@/mixins/focus.mixin';
import TimeAgo from '@/components/TimeAgo';
import TimeAgoReverse from '@/components/TimeAgoReverse';

export default {
  name: 'SuperAdminUsers',
  components: {TimeAgoReverse, TimeAgo, TableList},
  mixins: [TableMixin, FocusMixin],
  methods: {
    async update(options) {
      this.limit = options.itemsPerPage;
      this.skip = options.itemsPerPage * (options.page - 1);
      this.sort = this.getSortFromOptions(options);
      return this.load();
    },
    async load() {
      this.loading = true;
      const {list, total} = (await SuperAdmin.getUsers({
        skip: this.skip,
        limit: this.limit,
        sort: this.sort,
        search: this.search
      }));
      this.users = list;
      this.total = total;
      this.loading = false;
    },
    to(route) {
      this.$router.push(route);
    },
    async assumeUser(id) {
      const user = await SuperAdmin.assumeUser(id);
      await this.userAssumeUser(user);
      this.to('/');
    },
    async detailItem(id) {
      this.to({path: `users/${id}`});
    },
    async disableUser(user) {
      try {
        const action = user.enabled ? 'disable' : 'enable';
        if (!(await Notifications.confirm(action, `Are you sure you want to ${action} <strong>${user.email}</strong>?`))) return;
        const enabled = !user.enabled;
        user.enabled = (await SuperAdmin.editUser(user._id, {enabled})).enabled;
        Notifications.toast(`<strong>${user.email}</strong> ${action}d`);
      } catch (error) {
        Notifications.alertError('Unable to disable user', error);
      }
    },
    ...mapActions({userAssumeUser: 'userAssumeUser'})
  },
  watch: {
    // eslint-disable-next-line
    search: _.debounce(function () {
      this.load();
    }, 250)
  },
  data() {
    return {
      newUser: {},
      users: [],
      user: {},
      search: '',
      total: 0,
      loading: false,
      showCreateDialog: false,
      skip: 0,
      limit: 10,
      sort: null,
      defaultSort: {account: 1, email: 1},
      headers: [
        {text: 'Email', value: 'email'},
        {text: 'First Name', value: 'firstName'},
        {text: 'Last Name', value: 'lastName'},
        {text: 'Account', value: 'account'},
        {text: 'AccountAdmin', value: 'accountAdmin'},
        {text: 'Git Branch', value: 'gitBranch'},
        {text: 'Last Active', value: 'lastActive'},
        {text: 'Created', value: 'createdAt'},
        {text: 'Enabled', value: 'enabled'},
        {text: '', value: 'actions', sortable: false}
      ]
    };
  }
};
</script>

<style lang="scss" scoped>
</style>
